import { Activity, Layers, Users } from "lucide-react";
import { useEffect, useRef } from "react";

import type { GtmBrain } from "../api/GtmApi";
import { signalSections, type SignalDocId } from "./signalsProfile";

const DOC_META: Record<
  SignalDocId,
  { title: string; subtitle: string; icon: SignalDocId }
> = {
  hiring: {
    title: "Hiring",
    subtitle: "Roles and hiring patterns that mark a good fit.",
    icon: "hiring",
  },
  stack: {
    title: "Stack",
    subtitle: "Tools and stack signals that mark a good fit.",
    icon: "stack",
  },
};

type Props = {
  brain: GtmBrain;
  docId: SignalDocId;
  onBackToSignals: () => void;
  onBackToOverview: () => void;
};

export default function BrainSignalDocView({
  brain,
  docId,
  onBackToSignals,
  onBackToOverview,
}: Props) {
  const meta = DOC_META[docId];
  const sections = signalSections(brain, docId);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [docId]);

  const scrollToSection = (id: string) => {
    const el = scrollRef.current?.querySelector(`[data-section-id="${id}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const Icon = meta.icon === "stack" ? Layers : Users;

  return (
    <div className="flex h-full w-[min(100%,28rem)] shrink-0 min-w-0 flex-col border-r border-slate-200 bg-white">
      <div className="border-b border-slate-100 px-5 py-3 text-xs text-slate-500">
        <button
          type="button"
          className="hover:text-slate-800 hover:underline"
          onClick={onBackToOverview}
        >
          Brain
        </button>
        <span className="mx-1.5 text-slate-300">›</span>
        <button
          type="button"
          className="hover:text-slate-800 hover:underline"
          onClick={onBackToSignals}
        >
          Company signals
        </button>
        <span className="mx-1.5 text-slate-300">›</span>
        <span className="font-medium text-slate-800">{meta.title}</span>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6">
        <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50">
          <Icon className="h-4 w-4 text-amber-700" />
        </div>
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-amber-700" />
          <h2 className="text-xl font-semibold text-slate-900">{meta.title}</h2>
        </div>
        <p className="mt-1 text-sm text-slate-500">{meta.subtitle}</p>

        <div className="mt-4 flex flex-wrap gap-1.5">
          {sections.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => scrollToSection(s.id)}
              className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-600 transition-colors hover:border-slate-300 hover:bg-white hover:text-slate-900"
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="mt-8 space-y-8">
          {sections.map((s) => (
            <section key={s.id} data-section-id={s.id} className="scroll-mt-4">
              <h3 className="text-sm font-semibold text-slate-900">{s.label}</h3>
              {s.body.trim() ? (
                <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
                  {s.body.trim()}
                </p>
              ) : (
                <p className="mt-2 text-sm italic text-slate-400">Not filled yet.</p>
              )}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
